import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, BladApi } from '../api/client';
import { useLista } from '../api/lista';
import type {
  Alert, Dostawca, Egzemplarz, Lista, RodzajSerwisu, StatusSerwisu, ZdarzenieSerwisowe,
} from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import {
  Karta, Komunikat, Ladowanie, Plakietka, Pole, PoleTekstowe, Przycisk, Pusto, Wybor, Zakladki,
} from '../components/ui';
import { PasekWspolpracy } from '../components/PasekWspolpracy';
import { nazwaZListy, useEdycjaWspolna } from '../lib/wspolna-edycja';
import { PrzyciskExcel } from '../components/PrzyciskExcel';
import { PILNOSC, RODZAJ_ALERTU, RODZAJ_SERWISU, STATUS_SERWISU } from '../lib/slowniki-serwis';
import { data as fData, zlote } from '../lib/format';

export function Serwis() {
  const { maUprawnienie } = useAuth();
  const mozeEdytowac = maUprawnienie('serwis.zapis');
  const [zakladka, setZakladka] = useState<'alerty' | 'zdarzenia'>('alerty');
  const [formularz, setFormularz] = useState<ZdarzenieSerwisowe | 'nowe' | null>(null);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">Przeglądy i serwis</h1>
          <p className="mt-1 text-sm text-kamien-500">
            Terminy przeglądów, końce gwarancji i daty ważności w jednym miejscu.
            Sprzęt oddany do serwisu znika z dostępnych do rezerwacji.
          </p>
        </div>
        {mozeEdytowac && (
          <Przycisk wariant="glowny" onClick={() => setFormularz('nowe')}>Nowe zdarzenie</Przycisk>
        )}
      </div>

      <Zakladki
        wartosc={zakladka} zmien={setZakladka}
        opcje={[
          { wartosc: 'alerty', etykieta: 'Alerty' },
          { wartosc: 'zdarzenia', etykieta: 'Historia serwisowa' },
        ]}
      />

      {formularz && (
        <FormularzSerwisu
          key={formularz === 'nowe' ? 'nowe' : formularz.id}
          zdarzenie={formularz === 'nowe' ? null : formularz}
          zamknij={() => setFormularz(null)}
        />
      )}

      {zakladka === 'alerty' ? <PanelAlertow /> : <ListaZdarzen onEdytuj={setFormularz} mozeEdytowac={mozeEdytowac} />}
    </div>
  );
}

function PanelAlertow() {
  const { data, isLoading } = useQuery({
    queryKey: ['serwis', 'alerty'],
    queryFn: () => api.get<{ pozycje: Alert[]; podsumowanie: Record<string, number> }>('/serwis/alerty'),
    refetchInterval: 60_000,
  });

  if (isLoading) return <Ladowanie />;
  const pozycje = data?.pozycje ?? [];

  return (
    <div className="flex flex-col gap-4">
      <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-lg bg-kamien-200 ring-1 ring-kamien-200 sm:grid-cols-4">
        {(['ZALEGLY', 'PILNY', 'BLISKI', 'ODLEGLY'] as const).map((p) => (
          <div key={p} className="bg-white px-4 py-3">
            <dt className="text-[11px] uppercase tracking-wide text-kamien-500">{PILNOSC[p].etykieta}</dt>
            <dd className={`mt-0.5 text-xl font-semibold ${PILNOSC[p].klasa}`}>
              {data?.podsumowanie[p] ?? 0}
            </dd>
          </div>
        ))}
      </dl>

      <Karta>
        {pozycje.length === 0 ? (
          <Pusto tytul="Nic nie wymaga uwagi"
            opis="Żaden przegląd, gwarancja ani termin ważności nie zbliża się do końca." />
        ) : (
          <ul className="divide-y divide-kamien-200">
            {pozycje.map((a, i) => (
              <li key={`${a.rodzaj}-${a.equipmentUnitId ?? a.itemId}-${i}`}
                className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                <Plakietka ton={PILNOSC[a.pilnosc].ton}>
                  {a.dni < 0 ? `${Math.abs(a.dni)} dni po terminie` : `za ${a.dni} dni`}
                </Plakietka>
                <span className="text-xs uppercase tracking-wide text-kamien-400">
                  {RODZAJ_ALERTU[a.rodzaj]}
                </span>
                <span className="flex-1 text-kamien-900">{a.tytul}</span>
                <span className="text-xs text-kamien-500">{a.opis}</span>
                <span className="text-kamien-600">{fData(a.termin)}</span>
              </li>
            ))}
          </ul>
        )}
      </Karta>
    </div>
  );
}

function ListaZdarzen({ onEdytuj, mozeEdytowac }: {
  onEdytuj: (z: ZdarzenieSerwisowe) => void; mozeEdytowac: boolean;
}) {
  const widziCeny = useAuth().maUprawnienie('ceny.odczyt');
  const [status, setStatus] = useState<StatusSerwisu | ''>('');
  const parametry = new URLSearchParams({ ...(status ? { status } : {}), naStronie: '100' });

  const { data, isLoading } = useQuery({
    queryKey: ['serwis', parametry.toString()],
    queryFn: () => api.get<Lista<ZdarzenieSerwisowe>>(`/serwis?${parametry}`),
  });

  if (isLoading) return <Ladowanie />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="w-full max-w-xs">
          <Wybor etykieta="Stan zdarzenia" value={status}
            onChange={(e) => setStatus(e.target.value as StatusSerwisu | '')}>
            <option value="">wszystkie</option>
            {Object.entries(STATUS_SERWISU).map(([k, v]) => (
              <option key={k} value={k}>{v.etykieta}</option>
            ))}
          </Wybor>
        </div>
        <PrzyciskExcel dzial="serwis" parametry={parametry} />
      </div>

      <Karta>
        {data && data.pozycje.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
                <tr>
                  <th className="px-5 py-3 font-medium">Egzemplarz</th>
                  <th className="px-5 py-3 font-medium">Rodzaj</th>
                  <th className="px-5 py-3 font-medium">Wykonano</th>
                  <th className="px-5 py-3 font-medium">Następny termin</th>
                  <th className="px-5 py-3 font-medium">Wykonawca</th>
                  {widziCeny && <th className="px-5 py-3 text-right font-medium">Koszt</th>}
                  <th className="px-5 py-3 font-medium">Stan</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-kamien-200">
                {data.pozycje.map((z) => (
                  <tr key={z.id}
                    onClick={() => mozeEdytowac && onEdytuj(z)}
                    className={mozeEdytowac ? 'cursor-pointer hover:bg-kamien-50' : ''}>
                    <td className="px-5 py-3">
                      <span className="font-mono text-xs text-kamien-900">{z.equipmentUnit.nrCsm}</span>
                      <span className="block text-xs text-kamien-500">{z.equipmentUnit.item.name}</span>
                    </td>
                    <td className="px-5 py-3 text-kamien-700">{RODZAJ_SERWISU[z.type]}</td>
                    <td className="px-5 py-3 text-kamien-600">{fData(z.performedDate)}</td>
                    <td className="px-5 py-3 text-kamien-600">{fData(z.nextDueDate)}</td>
                    <td className="px-5 py-3 text-xs text-kamien-500">{z.technician ?? '—'}</td>
                    {widziCeny && <td className="px-5 py-3 text-right text-kamien-700">{zlote(z.cost)}</td>}
                    <td className="px-5 py-3">
                      <Plakietka ton={STATUS_SERWISU[z.status].ton}>
                        {STATUS_SERWISU[z.status].etykieta}
                      </Plakietka>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Pusto tytul="Brak zdarzeń serwisowych"
            opis="Zaplanuj przegląd albo odnotuj naprawę, żeby zbudować historię sprzętu." />
        )}
      </Karta>
    </div>
  );
}

const PUSTE = {
  equipmentUnitId: '', type: 'PRZEGLAD' as RodzajSerwisu, status: 'ZAPLANOWANY' as StatusSerwisu,
  plannedDate: '', performedDate: '', nextDueDate: '', supplierId: '', technician: '',
  faultDescription: '', workDescription: '', cost: '', downtimeFrom: '', downtimeTo: '',
  notes: '', ustawStanTechniczny: '',
};

const naDzien = (v: string | null) => (v ? v.slice(0, 10) : '');

function FormularzSerwisu({ zdarzenie, zamknij }: {
  zdarzenie: ZdarzenieSerwisowe | null; zamknij: () => void;
}) {
  const qc = useQueryClient();
  const widziCeny = useAuth().maUprawnienie('ceny.odczyt');
  const edycja = useEdycjaWspolna<ZdarzenieSerwisowe, typeof PUSTE>({
    encja: 'serwis', rekord: zdarzenie, pusty: PUSTE,
    doFormularza: (z) => ({
      equipmentUnitId: String(z.equipmentUnit.id),
      type: z.type, status: z.status,
      plannedDate: naDzien(z.plannedDate),
      performedDate: naDzien(z.performedDate),
      nextDueDate: naDzien(z.nextDueDate),
      supplierId: String(z.supplier?.id ?? ''),
      technician: z.technician ?? '',
      faultDescription: z.faultDescription ?? '',
      workDescription: z.workDescription ?? '',
      cost: z.cost ?? '',
      downtimeFrom: naDzien(z.downtimeFrom),
      downtimeTo: naDzien(z.downtimeTo),
      notes: z.notes ?? '',
      ustawStanTechniczny: '',
    }),
    wczytaj: (id) => api.get<ZdarzenieSerwisowe>(`/serwis/${id}`),
    etykiety: {
      type: 'Rodzaj', status: 'Status', plannedDate: 'Data planowana', performedDate: 'Data wykonania',
      nextDueDate: 'Następny termin', supplierId: 'Dostawca usługi', technician: 'Wykonawca',
      faultDescription: 'Opis usterki', workDescription: 'Zakres prac', cost: 'Koszt',
      downtimeFrom: 'Niedostępny od', downtimeTo: 'Niedostępny do', notes: 'Uwagi',
      ustawStanTechniczny: 'Zmiana stanu technicznego',
    },
    pokazWartosc: (pole, v) => (pole === 'supplierId'
      ? nazwaZListy(dostawcy.pozycje, v, (d) => d.name) : undefined),
  });
  const { dane, ustaw: setDane } = edycja;
  const [blad, setBlad] = useState<string | null>(null);
  const [bledy, setBledy] = useState<Record<string, string>>({});

  const sprzet = useLista({
    queryKey: ['sprzet', 'do-serwisu'],
    queryFn: () => api.get<Lista<Egzemplarz>>('/sprzet?naStronie=300'),
  });
  const dostawcy = useLista({
    queryKey: ['dostawcy'], queryFn: () => api.get<{ pozycje: Dostawca[] }>('/dostawcy'),
  });

  const zapisz = useMutation({
    mutationFn: () => edycja.zapisz((d, wersja) => {
      const cialo = {
        ...d,
        equipmentUnitId: Number(d.equipmentUnitId),
        supplierId: d.supplierId || null,
        cost: d.cost || null,
        ustawStanTechniczny: d.ustawStanTechniczny || undefined,
        ...(zdarzenie ? { version: wersja } : {}),
      };
      return zdarzenie
        ? api.put<ZdarzenieSerwisowe>(`/serwis/${zdarzenie.id}`, cialo)
        : api.post<ZdarzenieSerwisowe>('/serwis', cialo);
    }),
    onSuccess: (zapisane) => {
      void qc.invalidateQueries({ queryKey: ['serwis'] });
      void qc.invalidateQueries({ queryKey: ['sprzet'] });
      edycja.poZapisie(zapisane, zamknij);
    },
    onError: (e) => {
      if (e instanceof BladApi) {
        setBledy(e.bledyPol);
        setBlad(Object.keys(e.bledyPol).length === 0 ? e.message : e.message);
      }
    },
  });

  return (
    <Karta tytul={zdarzenie ? `Zdarzenie serwisowe #${zdarzenie.id}` : 'Nowe zdarzenie serwisowe'}>
      <form className="grid gap-4 px-5 py-5 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={(e) => { e.preventDefault(); setBlad(null); setBledy({}); zapisz.mutate(); }}>
 
        {blad && <div className="sm:col-span-2 lg:col-span-3"><Komunikat rodzaj="blad">{blad}</Komunikat></div>}
        <PasekWspolpracy edycja={edycja} />

        <div className="sm:col-span-2">
          <Wybor etykieta="Egzemplarz" value={dane.equipmentUnitId}
            blad={bledy.equipmentUnitId ?? sprzet.blad}
            onChange={(e) => setDane({ ...dane, equipmentUnitId: e.target.value })}
            disabled={!!zdarzenie} required>
            <option value="">— wybierz —</option>
            {sprzet.pozycje.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nrCsm} — {e.item.name}{e.model ? ` (${e.model})` : ''}
              </option>
            ))}
          </Wybor>
        </div>

        <Wybor etykieta="Rodzaj" value={dane.type} blad={bledy.type}
          onChange={(e) => setDane({ ...dane, type: e.target.value as RodzajSerwisu })}>
          {Object.entries(RODZAJ_SERWISU).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Wybor>
        <Wybor etykieta="Stan zdarzenia" value={dane.status} blad={bledy.status}
          onChange={(e) => setDane({ ...dane, status: e.target.value as StatusSerwisu })}>
          {Object.entries(STATUS_SERWISU).map(([k, v]) => <option key={k} value={k}>{v.etykieta}</option>)}
        </Wybor>
        <Pole etykieta="Planowana data" type="date" value={dane.plannedDate} blad={bledy.plannedDate}
          onChange={(e) => setDane({ ...dane, plannedDate: e.target.value })} />
        <Pole etykieta="Data wykonania" type="date" value={dane.performedDate} blad={bledy.performedDate}
          onChange={(e) => setDane({ ...dane, performedDate: e.target.value })} />
        <Pole etykieta="Następny termin" type="date" value={dane.nextDueDate} blad={bledy.nextDueDate}
          onChange={(e) => setDane({ ...dane, nextDueDate: e.target.value })}
          podpowiedz="Z tej daty biorą się alerty 30 / 14 / 7 dni" />

        <Wybor etykieta="Serwisant (dostawca)" value={dane.supplierId}
          blad={bledy.supplierId ?? dostawcy.blad}
          onChange={(e) => setDane({ ...dane, supplierId: e.target.value })}>
          <option value="">— nie wskazano —</option>
          {dostawcy.pozycje.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </Wybor>
        <Pole etykieta="Osoba wykonująca" value={dane.technician} blad={bledy.technician}
          onChange={(e) => setDane({ ...dane, technician: e.target.value })} />
        {widziCeny && (
          <Pole etykieta="Koszt (zł)" type="number" step="0.01" min="0" value={dane.cost} blad={bledy.cost}
            onChange={(e) => setDane({ ...dane, cost: e.target.value })} />
        )}

        <Pole etykieta="Niedostępny od" type="date" value={dane.downtimeFrom} blad={bledy.downtimeFrom}
          onChange={(e) => setDane({ ...dane, downtimeFrom: e.target.value })}
          podpowiedz="W tym okresie sprzęt nie da się zarezerwować" />
        <Pole etykieta="Niedostępny do" type="date" value={dane.downtimeTo} blad={bledy.downtimeTo}
          onChange={(e) => setDane({ ...dane, downtimeTo: e.target.value })} />
        <Wybor etykieta="Przestaw stan techniczny" value={dane.ustawStanTechniczny}
          blad={bledy.ustawStanTechniczny}
          onChange={(e) => setDane({ ...dane, ustawStanTechniczny: e.target.value })}>
          <option value="">— zostaw bez zmian —</option>
          <option value="SPRAWNY">sprawny</option>
          <option value="USZKODZONY">uszkodzony</option>
          <option value="W_NAPRAWIE">w naprawie</option>
          <option value="WYCOFANY">wycofany</option>
        </Wybor>

        <div className="sm:col-span-2 lg:col-span-3">
          <PoleTekstowe etykieta="Opis usterki" value={dane.faultDescription}
            onChange={(e) => setDane({ ...dane, faultDescription: e.target.value })} />
        </div>
        <div className="sm:col-span-2 lg:col-span-3">
          <PoleTekstowe etykieta="Wykonane czynności" value={dane.workDescription}
            onChange={(e) => setDane({ ...dane, workDescription: e.target.value })} />
        </div>

        <div className="flex gap-3 sm:col-span-2 lg:col-span-3">
          <Przycisk type="submit" wariant="glowny" disabled={zapisz.isPending}>
            {zapisz.isPending ? 'Zapisywanie...' : zdarzenie ? 'Zapisz zmiany' : 'Dodaj zdarzenie'}
          </Przycisk>
          <Przycisk type="button" wariant="cichy" onClick={zamknij}>Anuluj</Przycisk>
        </div>
      </form>
    </Karta>
  );
}
